import React from 'react';
import { useStore, DocumentItem, Diferencial, MotivoEscolha } from '../../store/useStore';
import { RichTextEditor, DocumentUpload, Tabs, Repeater, Input, Textarea } from '../ui';
import { Briefcase, FileHeart, Award, FileText } from 'lucide-react';

export function M3_BusinessDetails() {
  const { businessDetails, setBusinessDetails } = useStore();

  const handleAddDocument = () => {
    const novelItem: DocumentItem = { id: crypto.randomUUID(), nome: '', descricao: '', files: [] };
    setBusinessDetails({ documentos: [...businessDetails.documentos, novelItem] });
  };

  const handleUpdateDocument = (index: number, key: keyof DocumentItem, value: any) => {
    const updated = [...businessDetails.documentos];
    updated[index] = { ...updated[index], [key]: value };
    setBusinessDetails({ documentos: updated });
  };

  const handleRemoveDocument = (index: number) => {
    const updated = businessDetails.documentos.filter((_, i) => i !== index);
    setBusinessDetails({ documentos: updated });
  };

  const handleAddDiferencial = () => {
    const novelItem: Diferencial = { id: crypto.randomUUID(), titulo: '', descricao: '' };
    setBusinessDetails({ diferenciais: [...businessDetails.diferenciais, novelItem] });
  };

  const handleUpdateDiferencial = (index: number, key: keyof Diferencial, value: any) => {
    const updated = [...businessDetails.diferenciais];
    updated[index] = { ...updated[index], [key]: value };
    setBusinessDetails({ diferenciais: updated });
  };

  const handleRemoveDiferencial = (index: number) => {
    const updated = businessDetails.diferenciais.filter((_, i) => i !== index);
    setBusinessDetails({ diferenciais: updated });
  };

  const handleAddMotivo = (type: 'porqueEscolhem' | 'porqueNaoConcorrentes') => {
    const novelItem: MotivoEscolha = { id: crypto.randomUUID(), motivo: '', descricao: '' };
    setBusinessDetails({ [type]: [...businessDetails[type], novelItem] });
  };

  const handleUpdateMotivo = (type: 'porqueEscolhem' | 'porqueNaoConcorrentes', index: number, key: keyof MotivoEscolha, value: any) => {
    const updated = [...businessDetails[type]];
    updated[index] = { ...updated[index], [key]: value };
    setBusinessDetails({ [type]: updated });
  };

  const handleRemoveMotivo = (type: 'porqueEscolhem' | 'porqueNaoConcorrentes', index: number) => {
    const updated = businessDetails[type].filter((_, i) => i !== index);
    setBusinessDetails({ [type]: updated });
  };

  const tabs = [
    {
      id: 'docs',
      label: 'Documentação',
      icon: <FileText size={16} />,
      content: (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6">
          <Repeater<DocumentItem>
            title="Documentos da Empresa"
            description="Adicione materiais, catálogos, manuais ou apresentações institucionais. Quantos quiser."
            addButtonText="Novo Documento"
            items={businessDetails.documentos}
            onAdd={handleAddDocument}
            onRemove={handleRemoveDocument}
            getItemSummary={(item) => ({ 
              title: item.nome || 'Novo Documento', 
              subtitle: `${item.files?.length || 0} arquivo(s) anexado(s)` 
            })}
            renderItem={(item, index) => (
              <div className="space-y-4">
                <Input 
                  label="Nome do Documento / Categoria" 
                  value={item.nome} 
                  onChange={(e) => handleUpdateDocument(index, 'nome', e.target.value)} 
                  placeholder="Ex: Apresentação Institucional, Tabela de Preços..." 
                />
                <Textarea 
                  label="Descrição (Opcional)" 
                  value={item.descricao} 
                  onChange={(e) => handleUpdateDocument(index, 'descricao', e.target.value)} 
                  placeholder="Do que se trata esse documento?"
                  className="min-h-[80px]"
                />
                <DocumentUpload 
                  label="Arquivos" 
                  maxFiles={10} 
                  onChange={(files) => handleUpdateDocument(index, 'files', files)} 
                />
              </div>
            )}
          />
        </div>
      )
    },
    {
      id: 'sobre',
      label: 'Sobre a Empresa',
      icon: <Briefcase size={16} />,
      content: (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6 space-y-6">
          <RichTextEditor 
            label="O que a empresa faz (Elevator Pitch)" 
            value={businessDetails.oQueFaz} 
            onChange={(val) => setBusinessDetails({ oQueFaz: val })} 
            placeholder="Descreva em poucas palavras o core business do cliente..." 
            minHeight="min-h-[150px]"
          />
          <RichTextEditor 
            label="História da Empresa" 
            value={businessDetails.historia} 
            onChange={(val) => setBusinessDetails({ historia: val })} 
            placeholder="Como o negócio começou? Qual a trajetória?" 
            minHeight="min-h-[250px]"
          />
        </div>
      )
    },
    {
      id: 'cultura',
      label: 'Cultura (MVC)',
      icon: <FileHeart size={16} />,
      content: (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
           <RichTextEditor label="Missão" value={businessDetails.missao} onChange={(val) => setBusinessDetails({ missao: val })} placeholder="A razão de existir..." minHeight="min-h-[200px]" />
           <RichTextEditor label="Visão" value={businessDetails.visao} onChange={(val) => setBusinessDetails({ visao: val })} placeholder="Onde quer chegar?" minHeight="min-h-[200px]" />
           <RichTextEditor label="Valores" value={businessDetails.valores} onChange={(val) => setBusinessDetails({ valores: val })} placeholder="Princípios inegociáveis..." minHeight="min-h-[200px]" />
        </div>
      )
    },
    {
      id: 'diferenciais',
      label: 'Posicionamento e Diferenciais',
      icon: <Award size={16} />,
      content: (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden space-y-2">
            <div className="p-6 border-b border-gray-100">
              <Repeater<Diferencial>
                title="Principais Diferenciais Competitivos"
                description="O que destaca a empresa no mercado em relação às demais."
                addButtonText="Novo Diferencial"
                items={businessDetails.diferenciais}
                onAdd={handleAddDiferencial}
                onRemove={handleRemoveDiferencial}
                getItemSummary={(item) => ({ 
                  title: item.titulo || 'Novo Diferencial', 
                  subtitle: item.descricao 
                })}
                renderItem={(item, index) => (
                  <div className="space-y-4">
                     <Input 
                       label="Título do Diferencial" 
                       value={item.titulo} 
                       onChange={(e) => handleUpdateDiferencial(index, 'titulo', e.target.value)} 
                       placeholder="Ex: Atendimento Premium 24h"
                     />
                     <Textarea 
                       label="Descrição do Diferencial" 
                       value={item.descricao} 
                       onChange={(e) => handleUpdateDiferencial(index, 'descricao', e.target.value)} 
                       placeholder="Por que isso é um diferencial na prática?"
                       className="min-h-[80px]"
                     />
                  </div>
                )}
              />
            </div>

            <div className="p-6 grid grid-cols-1 xl:grid-cols-2 gap-8">
                <div className="space-y-4">
                   <Repeater<MotivoEscolha>
                     title="Por que os clientes escolhem vocês?"
                     addButtonText="Adicionar Motivo"
                     items={businessDetails.porqueEscolhem}
                     onAdd={() => handleAddMotivo('porqueEscolhem')}
                     onRemove={(index) => handleRemoveMotivo('porqueEscolhem', index)}
                     getItemSummary={(item) => ({ 
                       title: item.motivo || 'Novo Motivo', 
                       subtitle: item.descricao 
                     })}
                     renderItem={(item, index) => (
                        <div className="space-y-4">
                           <Input 
                            label="Motivo central" 
                            value={item.motivo} 
                            onChange={(e) => handleUpdateMotivo('porqueEscolhem', index, 'motivo', e.target.value)} 
                            placeholder="Ex: Confiança na marca"
                           />
                           <Textarea 
                            label="Explicação (Opcional)" 
                            value={item.descricao} 
                            onChange={(e) => handleUpdateMotivo('porqueEscolhem', index, 'descricao', e.target.value)} 
                            placeholder="Descreva..."
                            className="min-h-[80px]"
                           />
                        </div>
                     )}
                   />
                </div>
                <div className="space-y-4">
                   <Repeater<MotivoEscolha>
                     title="Por que NÃO escolhem os concorrentes?"
                     addButtonText="Adicionar Motivo"
                     items={businessDetails.porqueNaoConcorrentes}
                     onAdd={() => handleAddMotivo('porqueNaoConcorrentes')}
                     onRemove={(index) => handleRemoveMotivo('porqueNaoConcorrentes', index)}
                     getItemSummary={(item) => ({ 
                       title: item.motivo || 'Novo Motivo', 
                       subtitle: item.descricao 
                     })}
                     renderItem={(item, index) => (
                        <div className="space-y-4">
                           <Input 
                            label="O que o concorrente peca?" 
                            value={item.motivo} 
                            onChange={(e) => handleUpdateMotivo('porqueNaoConcorrentes', index, 'motivo', e.target.value)} 
                            placeholder="Ex: Suporte lento"
                           />
                           <Textarea 
                            label="Explicação (Opcional)" 
                            value={item.descricao} 
                            onChange={(e) => handleUpdateMotivo('porqueNaoConcorrentes', index, 'descricao', e.target.value)} 
                            placeholder="Descreva..."
                            className="min-h-[80px]"
                           />
                        </div>
                     )}
                   />
                </div>
            </div>
        </div>
      )
    }
  ];

  return <Tabs tabs={tabs} />;
}
